import { uniq } from 'es-toolkit';
import React from 'react';
import { Divider, Stack } from '@mui/material';

import { INDETERMINATE, isIndeterminate } from '@paragon/client-tools';
import { CeilingTray } from '@paragon/contract-library';
import { distanceLessThan } from '@paragon/geometry-library';
import { AngleField, DistanceField } from '@paragon/material';

import { TransformationButtons } from '../../../../../library/transformations/controls/TransformationButtons';
import { CeilingTrayEditInstructions } from '../../../../../store/actions/layout/ceilingTrayActions';
import {
  ceilingTrayElevationMatches,
  getActiveLayoutLevelGuidOrNull,
  getCeilingTrayElevationFromActiveLevel,
} from '../../../../../store/selectors/layoutDataSelectors';
import { InsertionCeilingTray } from '../../../../../store/slices/applicationSlice';
import { LayoutRectangleControls } from '../../../../controls/layout-shapes/LayoutRectangleControls';
import { useAppSelector } from '../../../../hooks';
import { ProjectHierarchyContextSelects } from '../ProjectHierarchyContextSelects';
import { SyncElevationButton } from '../SyncElevationButton';

/*
 * Note that we strongly desire to keep this panel as visually similar to the plural `BulkCeilingTrayPropertyControls`
 *  as possible. Please consider updating both controls when making any changes.
 */
export function CeilingTrayPropertyControls(props: {
  readonly tray: InsertionCeilingTray;
  readonly onChange: (patch: Partial<Exclude<CeilingTray, 'guid'>>) => void;
  readonly disabled?: boolean;
}) {
  const elevationsFromLevel = useAppSelector(getCeilingTrayElevationFromActiveLevel);

  return (
    <Stack spacing={2}>
      <TransformationButtons />

      <Divider />

      <ProjectHierarchyContextSelects
        levelGuid={props.tray.levelGuid}
        onChange={(levelGuid) => {
          props.onChange({ levelGuid });
        }}
        disabled={props.disabled}
      />

      <SyncElevationButton
        disabled={props.disabled}
        visible={props.tray.levelGuid != null && elevationsFromLevel.bottomElevation != null}
        synchronized={ceilingTrayElevationMatches(props.tray, elevationsFromLevel)}
        onSynchronize={() => {
          props.onChange(elevationsFromLevel);
        }}
      />

      <Stack direction="row" spacing={1} sx={{ marginTop: '24px!important' }}>
        <DistanceField
          label="Bottom Elevation"
          value={props.tray.bottomElevation ?? null}
          onSubmit={(bottomElevation) => {
            props.onChange({ bottomElevation });
          }}
          disabled={props.disabled}
        />

        <DistanceField
          label="Top Elevation"
          value={props.tray.topElevation ?? null}
          validator={(topElevation) => {
            if (distanceLessThan(topElevation, props.tray.bottomElevation)) {
              return 'Must be above bottom elevation';
            }
          }}
          onSubmit={(topElevation) => {
            props.onChange({ topElevation });
          }}
          disabled={props.disabled}
        />
      </Stack>

      <Divider />

      <LayoutRectangleControls
        value={props.tray}
        positionTarget="ceiling-tray-position"
        onChange={(patch) => {
          props.onChange(patch);
        }}
        disabled={props.disabled}
      />

      <AngleField
        label="Side Angle"
        defaultDisplayFormat="slope"
        value={props.tray.sideAngle}
        onSubmit={(sideAngle) => {
          props.onChange({ sideAngle });
        }}
        disabled={props.disabled}
      />
    </Stack>
  );
}

/*
 * Note that we strongly desire to keep this panel as visually similar to the singular `CeilingTrayPropertyControls`
 *  as possible. Please consider updating both controls when making any changes.
 */
export function BulkCeilingTrayPropertyControls(props: {
  readonly trays: CeilingTray[];
  readonly onChange: (edits: CeilingTrayEditInstructions[]) => void;
  readonly disabled?: boolean;
}) {
  const activeLevelGuid = useAppSelector(getActiveLayoutLevelGuidOrNull);
  const elevationFromLevel = useAppSelector(getCeilingTrayElevationFromActiveLevel);

  const levelGuids = uniq(props.trays.map((tray) => tray.levelGuid));
  const commonLevelGuid = levelGuids.length === 1 ? levelGuids[0] : INDETERMINATE;

  const levelIsSuitableForSync =
    activeLevelGuid != null && commonLevelGuid === activeLevelGuid && elevationFromLevel.bottomElevation != null;

  const bottomElevations = uniq(props.trays.map((tray) => tray.bottomElevation));
  const commonBottomElevation = bottomElevations.length === 1 ? bottomElevations[0] : INDETERMINATE;

  const topElevations = uniq(props.trays.map((tray) => tray.topElevation));
  const commonTopElevation = topElevations.length === 1 ? topElevations[0] : INDETERMINATE;

  const sideAngles = uniq(props.trays.map((tray) => tray.sideAngle));
  const commonSideAngle = sideAngles.length === 1 ? sideAngles[0] : INDETERMINATE;

  return (
    <Stack spacing={2}>
      <TransformationButtons />

      <Divider />

      {!isIndeterminate(commonLevelGuid) && (
        <ProjectHierarchyContextSelects
          levelGuid={commonLevelGuid}
          onChange={(levelGuid) => {
            props.onChange(
              props.trays.map((tray) => ({
                guid: tray.guid,
                editedProperties: { levelGuid },
              })),
            );
          }}
          disabled={props.disabled}
        />
      )}

      {levelIsSuitableForSync && (
        <SyncElevationButton
          disabled={props.disabled}
          synchronized={
            typeof commonBottomElevation === 'number' &&
            ceilingTrayElevationMatches({ bottomElevation: commonBottomElevation }, elevationFromLevel)
          }
          onSynchronize={() => {
            props.onChange(
              props.trays.map((tray) => ({
                guid: tray.guid,
                editedProperties: elevationFromLevel,
              })),
            );
          }}
        />
      )}

      <Stack direction="row" spacing={1}>
        <DistanceField
          canBeIndeterminate
          label="Bottom Elevation"
          value={commonBottomElevation}
          onSubmit={(bottomElevation) => {
            props.onChange(
              props.trays.map((tray) => ({
                guid: tray.guid,
                editedProperties: { bottomElevation },
              })),
            );
          }}
          disabled={props.disabled}
        />

        <DistanceField
          canBeIndeterminate
          label="Top Elevation"
          value={commonTopElevation}
          onSubmit={(topElevation) => {
            props.onChange(
              props.trays.map((tray) => ({
                guid: tray.guid,
                editedProperties: { topElevation },
              })),
            );
          }}
          disabled={props.disabled}
        />
      </Stack>

      <Divider />

      <AngleField
        canBeIndeterminate
        label="Side Angle"
        defaultDisplayFormat="slope"
        value={commonSideAngle}
        onSubmit={(sideAngle) => {
          props.onChange(
            props.trays.map((tray) => ({
              guid: tray.guid,
              editedProperties: { sideAngle },
            })),
          );
        }}
        disabled={props.disabled}
      />
    </Stack>
  );
}
